import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Bell,
  Shield,
  DollarSign,
  CreditCard,
  TrendingUp,
  Gift,
  AlertTriangle,
  CheckCircle,
  Info,
  X,
  Filter,
  Search,
  MoreVertical,
  Star,
  Archive,
  Trash2,
  Menu,
  Loader2,
  RefreshCw
} from 'lucide-react'
import { NotificationsAPI } from '../services/api'

const notificationTypes = {
  security: { icon: Shield, color: 'bg-red-50 text-red-600' },
  transaction: { icon: DollarSign, color: 'bg-emerald-50 text-emerald-600' },
  card: { icon: CreditCard, color: 'bg-blue-50 text-blue-600' },
  investment: { icon: TrendingUp, color: 'bg-purple-50 text-purple-600' },
  promotion: { icon: Gift, color: 'bg-orange-50 text-orange-600' },
  alert: { icon: AlertTriangle, color: 'bg-red-50 text-red-600' },
  info: { icon: Info, color: 'bg-slate-50 text-slate-600' },
  account: { icon: CreditCard, color: 'bg-blue-50 text-blue-600' },
  reward: { icon: Gift, color: 'bg-purple-50 text-purple-600' },
  general: { icon: Bell, color: 'bg-slate-50 text-slate-600' }
}

export default function NotificationsCenter({ onMenuClick }) {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedNotifications, setSelectedNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchNotifications()
    fetchUnreadCount()
  }, [page, filter])

  const fetchNotifications = async () => {
    setLoading(true)
    try {
      const params = { page, limit: 20 }
      if (filter === 'unread') params.isRead = false
      if (filter !== 'all' && filter !== 'unread' && filter !== 'priority') {
        params.type = filter
      }

      const res = await NotificationsAPI.getAll(params)
      if (res.success) {
        let data = res.data
        if (filter === 'priority') {
          data = data.filter(n => n.priority === 'high' || n.priority === 'urgent')
        }
        setNotifications(data)
        if (res.pagination) {
          setTotalPages(res.pagination.totalPages)
        }
      }
    } catch (err) {
      console.error('Error fetching notifications:', err)
      setError('Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  const fetchUnreadCount = async () => {
    try {
      const res = await NotificationsAPI.getUnreadCount()
      if (res.success) {
        setUnreadCount(res.data.count)
      }
    } catch (err) {
      console.error('Error fetching unread count:', err)
    }
  }

  const markAsRead = async (id) => {
    try {
      const res = await NotificationsAPI.markAsRead(id)
      if (res.success) {
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === id ? { ...notif, is_read: true } : notif
          )
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (err) {
      console.error('Error marking as read:', err)
    }
  }

  const markAllAsRead = async () => {
    try {
      const res = await NotificationsAPI.markAllAsRead()
      if (res.success) {
        setNotifications(prev =>
          prev.map(notif => ({ ...notif, is_read: true }))
        )
        setUnreadCount(0)
      }
    } catch (err) {
      console.error('Error marking all as read:', err)
    }
  }

  const archiveNotification = async (id) => {
    try {
      const res = await NotificationsAPI.archive(id)
      if (res.success) {
        setNotifications(prev => prev.filter(notif => notif.id !== id))
      }
    } catch (err) {
      console.error('Error archiving notification:', err)
    }
  }

  const deleteNotification = async (id) => {
    try {
      const res = await NotificationsAPI.delete(id)
      if (res.success) {
        setNotifications(prev => prev.filter(notif => notif.id !== id))
      }
    } catch (err) {
      console.error('Error deleting notification:', err)
    }
  }

  const toggleSelection = (id) => {
    setSelectedNotifications(prev =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    )
  }

  const formatTimestamp = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes} minutes ago`
    if (hours < 24) return `${hours} hours ago`
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const filteredNotifications = notifications.filter(notification => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      notification.title?.toLowerCase().includes(search) ||
      notification.message?.toLowerCase().includes(search)
    )
  })

  if (loading && notifications.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading notifications...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Menu className="w-6 h-6 text-slate-700" />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-slate-900">Notifications</h1>
                <p className="text-xs md:text-sm text-slate-500 mt-0.5">{unreadCount} unread notifications</p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <button
                onClick={fetchNotifications}
                disabled={loading}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-5 h-5 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
                className="px-3 md:px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-xs md:text-sm font-medium disabled:opacity-50"
              >
                Mark All Read
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 md:mb-8"
        >
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-slate-300"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {['all', 'unread', 'priority', 'security', 'transaction', 'account'].map((filterType) => (
              <button
                key={filterType}
                onClick={() => { setFilter(filterType); setPage(1); }}
                className={`px-3 md:px-4 py-2 rounded-lg transition-colors capitalize text-xs md:text-sm font-medium whitespace-nowrap ${
                  filter === filterType
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {filterType}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Notifications List */}
        {error ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-xl">
            <AlertTriangle className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <h3 className="text-sm font-semibold text-slate-900 mb-2">{error}</h3>
            <button
              onClick={fetchNotifications}
              className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm"
            >
              Try Again
            </button>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-white border border-slate-200 rounded-xl"
          >
            <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-sm font-semibold text-slate-900 mb-2">No notifications found</h3>
            <p className="text-xs text-slate-500">Try adjusting your search or filter criteria</p>
          </motion.div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {filteredNotifications.map((notification, index) => {
                const typeConfig = notificationTypes[notification.type] || notificationTypes.general
                const NotificationIcon = typeConfig.icon
                const isSelected = selectedNotifications.includes(notification.id)

                return (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.03 }}
                    className={`group relative p-4 md:p-5 rounded-xl transition-all cursor-pointer ${
                      notification.is_read ? 'bg-white border border-slate-200' : 'bg-slate-50 border-2 border-slate-900'
                    } hover:border-slate-300`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    {/* Priority indicator */}
                    {(notification.priority === 'high' || notification.priority === 'urgent') && (
                      <div className="absolute top-4 right-4 w-2 h-2 bg-red-500 rounded-full" />
                    )}

                    <div className="flex items-start gap-3 md:gap-4">
                      {/* Icon */}
                      <div className={`p-2 md:p-2.5 rounded-lg ${typeConfig.color} flex-shrink-0`}>
                        <NotificationIcon className="w-4 h-4 md:w-5 md:h-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className={`font-semibold text-sm mb-1 ${
                              notification.is_read ? 'text-slate-700' : 'text-slate-900'
                            }`}>
                              {notification.title}
                            </h3>
                            <p className={`text-xs leading-relaxed mb-2 ${
                              notification.is_read ? 'text-slate-500' : 'text-slate-600'
                            }`}>
                              {notification.message}
                            </p>
                            <p className="text-xs text-slate-400">{formatTimestamp(notification.created_at)}</p>
                          </div>

                          {/* Amount if exists in data */}
                          {notification.data?.amount && (
                            <div className={`px-2 md:px-3 py-1 md:py-1.5 rounded-lg font-semibold text-xs ml-2 flex-shrink-0 ${
                              parseFloat(notification.data.amount) > 0
                                ? 'text-emerald-600 bg-emerald-50'
                                : 'text-slate-600 bg-slate-100'
                            }`}>
                              {parseFloat(notification.data.amount) > 0 ? '+' : ''}${Math.abs(parseFloat(notification.data.amount)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </div>
                          )}
                        </div>

                        {/* Action buttons if defined */}
                        {notification.action_url && (
                          <div className="mt-3">
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-900 hover:bg-slate-800 text-white transition-colors"
                            >
                              View Details
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Actions Menu */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            archiveNotification(notification.id)
                          }}
                          className="p-1.5 md:p-2 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Archive"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteNotification(notification.id)
                          }}
                          className="p-1.5 md:p-2 text-slate-400 hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Unread indicator */}
                    {!notification.is_read && (
                      <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-slate-900 rounded-r" />
                    )}
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-slate-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedNotifications.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-xl p-4 shadow-lg border border-slate-200"
          >
            <div className="flex items-center gap-4">
              <span className="text-slate-900 font-medium text-sm">
                {selectedNotifications.length} selected
              </span>
              <button className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors text-sm font-medium">
                Mark as Read
              </button>
              <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium">
                Archive
              </button>
              <button className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors text-sm font-medium">
                Delete
              </button>
              <button
                onClick={() => setSelectedNotifications([])}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}
